import axios, { AxiosResponse } from 'axios';

/**
 * API Configuration Constants
 */
const getApiBaseUrl = () => {
	const apiUrl = process.env.VITE_API_URL || import.meta.env.VITE_API_URL;

	if (import.meta.env.DEV) {
		return '/api/v1';
	}

	return `${apiUrl}/api/v1` || 'http://localhost:3000/api/v1';
};

const API_BASE_URL = getApiBaseUrl();

/**
 * Get authentication token from storage
 */
const getAuthToken = (): string | null => {
	return sessionStorage.getItem('auth_token');
};

/**
 * HTTP Endpoint API Key interfaces
 */
export interface HttpEndpointApiKey {
	id: string;
	keyId: string;
	name: string;
	description?: string;
	scopes: string[];
	rateLimitRpm: number;
	rateLimitRph: number;
	isActive: boolean;
	createdBy: string;
	lastUsedAt?: string;
	usageCount: number;
	expiresAt?: string;
	createdAt: string;
	updatedAt: string;
}

export interface CreateHttpEndpointApiKeyRequest {
	name: string;
	description?: string;
	scopes: string[];
	rateLimitRpm?: number;
	rateLimitRph?: number;
	expiresAt?: Date;
}

export interface CreateHttpEndpointApiKeyResponse {
	apiKey: HttpEndpointApiKey;
	plainSecret: string;
}

export interface UpdateHttpEndpointApiKeyRequest {
	name?: string;
	description?: string;
	rateLimitRpm?: number;
	rateLimitRph?: number;
	isActive?: boolean;
	expiresAt?: Date;
}

export interface ApiKeyStats {
	total: number;
	active: number;
	expired: number;
	totalUsage: number;
}

/**
 * API client for HTTP endpoint API key management
 */
class HttpEndpointApiKeysClient {
	private readonly timeout: number = 30000;

	/**
	 * Get authentication headers for API requests
	 */
	private getAuthHeaders(): Record<string, string> {
		const token = getAuthToken();
		return {
			'Content-Type': 'application/json',
			...(token && { Authorization: `Bearer ${token}` }),
		};
	}

	/**
	 * Handle API errors consistently
	 */
	private handleError(error: any): never {
		if (axios.isAxiosError(error)) {
			const errorMessage = error.response?.data?.message || error.message;
			throw new Error(errorMessage);
		}
		throw new Error(error.message || 'Unknown error occurred');
	}

	/**
	 * Create a new HTTP endpoint API key
	 */
	async createApiKey(
		data: CreateHttpEndpointApiKeyRequest
	): Promise<CreateHttpEndpointApiKeyResponse> {
		try {
			const response: AxiosResponse<CreateHttpEndpointApiKeyResponse> = await axios.post(
				`${API_BASE_URL}/composer/api-keys`,
				data,
				{
					headers: this.getAuthHeaders(),
					timeout: this.timeout,
				}
			);
			return response.data;
		} catch (error) {
			this.handleError(error);
		}
	}

	/**
	 * Get all API keys for the current user
	 */
	async getApiKeys(): Promise<HttpEndpointApiKey[]> {
		try {
			const response: AxiosResponse<HttpEndpointApiKey[]> = await axios.get(
				`${API_BASE_URL}/composer/api-keys`,
				{
					headers: this.getAuthHeaders(),
					timeout: this.timeout,
				}
			);
			return response.data;
		} catch (error) {
			this.handleError(error);
		}
	}

	/**
	 * Get API key usage statistics
	 */
	async getApiKeyStats(): Promise<ApiKeyStats> {
		try {
			const response: AxiosResponse<ApiKeyStats> = await axios.get(
				`${API_BASE_URL}/composer/api-keys/stats`,
				{
					headers: this.getAuthHeaders(),
					timeout: this.timeout,
				}
			);
			return response.data;
		} catch (error) {
			this.handleError(error);
		}
	}

	/**
	 * Update an API key
	 */
	async updateApiKey(
		keyId: string,
		data: UpdateHttpEndpointApiKeyRequest
	): Promise<HttpEndpointApiKey> {
		try {
			const response: AxiosResponse<HttpEndpointApiKey> = await axios.put(
				`${API_BASE_URL}/composer/api-keys/${keyId}`,
				data,
				{
					headers: this.getAuthHeaders(),
					timeout: this.timeout,
				}
			);
			return response.data;
		} catch (error) {
			this.handleError(error);
		}
	}

	/**
	 * Deactivate an API key
	 */
	async deactivateApiKey(keyId: string): Promise<{ message: string }> {
		try {
			const response: AxiosResponse<{ message: string }> = await axios.delete(
				`${API_BASE_URL}/composer/api-keys/${keyId}`,
				{
					headers: this.getAuthHeaders(),
					timeout: this.timeout,
				}
			);
			return response.data;
		} catch (error) {
			this.handleError(error);
		}
	}
}

// Export singleton instance
export const httpEndpointApiKeysClient = new HttpEndpointApiKeysClient();
